import type { Draft, DocumentRecord } from "../shared/domain";

const content = ({ version: _version, ...draft }: Draft) =>
  JSON.stringify(draft);

/** One writer per editor. Acknowledgements advance the version, never the user's text. */
export class DraftSaver {
  current: Draft;
  savedContent: string;
  record: DocumentRecord | null = null;
  private pending: Promise<DocumentRecord> | null = null;
  constructor(
    initial: Draft,
    private write: (draft: Draft) => Promise<DocumentRecord>,
    private acknowledge: (draft: Draft, record: DocumentRecord) => void,
  ) {
    this.current = initial;
    const hasWork =
      initial.clientId ||
      initial.customerReference ||
      initial.internalNote ||
      initial.lines.some((line) => line.description || line.netPence);
    this.savedContent =
      initial.version === 0 && hasWork ? "" : content(initial);
  }
  get dirty() {
    return content(this.current) !== this.savedContent;
  }
  update(draft: Draft) {
    this.current = { ...draft, version: this.current.version };
  }
  async flush(drain = true): Promise<DocumentRecord> {
    if (this.pending) {
      await this.pending;
      return drain ? this.flush() : this.record!;
    }
    if (!this.dirty && this.record) return this.record;
    const sent = this.current;
    this.pending = this.write(sent).then((record) => {
      this.record = record;
      this.savedContent = content(sent);
      this.current = { ...this.current, version: record.version };
      this.acknowledge(this.current, record);
      return record;
    });
    let result: DocumentRecord;
    try {
      result = await this.pending;
    } finally {
      this.pending = null;
    }
    return drain && this.dirty ? this.flush() : result;
  }
}
